import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BehaviorSubject,
  forkJoin,
  Observable,
  Subject,
  throwError,
} from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { getApiUrl } from 'src/app/shared/utils/apiUtils';
import { KEYS } from '../../../utils/constants/storage.constants';
import {
  getBaseRequestData,
  isSuccessfulResponse,
} from '../../../utils/evergentUtils';
import { ConfigService } from '../../config.service';
import { LocalStorageService } from '../../storage/local-storage.service';
import { SessionStorageService } from '../../storage/session-storage.service';
import { GetContactRequestMessage } from '../onboarding/model/get-profiles/get-contact-request.message';
import { GetContactRequestModel } from '../onboarding/model/get-profiles/get-contact-request.model';
import {
  AccountProfileSettingsRequestModel,
  GetAccountProfileRequestMessage,
} from './model/account-profile-settings.request.model';
import { GetAccountProfileUserResponseMessage } from './model/account-profile-subscription.response.model';
import { ProfilePreferences } from 'src/app/shared/interfaces/account.interfaces';
import countryCodesData from './country_code.json';
import { ContactMessageModel } from '../onboarding/model/get-profiles/get-contact-response.model';
import { GetContactResponseMessage } from '../onboarding/model/get-profiles/get-contact-response.message';
import { DataService } from 'src/app/data.service';
import {
  UpdateProfileRequest,
  UpdateProfileRequestMessage,
} from './model/account-profile-update.request.model';
import { UpdateProfileResponseMessage } from './model/account-profile-update.response.model';

@Injectable({
  providedIn: 'root',
})
export class AccountProfileService {
  /** Base URL for UMS API endpoints */
  private umsUrl: string;
  isUpgrade: boolean = false;

  /** cached  account profile  */
  private accountProfile!: GetAccountProfileUserResponseMessage;
  /** Cached list of contacts for the current account */
  private contactsResponse!: ContactMessageModel[];
  // Create a private BehaviorSubject for selected profile
  private _selectedProfileSubject =
    new BehaviorSubject<ContactMessageModel | null>(null);

  clean() {
    this.accountProfile = null;
    this.contactsResponse = null;
    this._selectedProfile = null;
    this._selectedProfileSubject.next(null);
    this.dataService.isParentalEnabled = false;
    this.sessionStorage.removeItem(KEYS.MAIN_CONTACT_ID);
    this.sessionStorage.removeItem(KEYS.MAIN_CONTACT);
    this.sessionStorage.removeItem(KEYS.ACCOUNT_CHANNEL_PARTNER_ID);
  }

  selectedProfileObs: Observable<ContactMessageModel | null> =
    this._selectedProfileSubject.asObservable();

  /** Subject for emitting navbar refresh events */
  private refreshNavbar$ = new Subject<void>();
  private _selectedProfile: ContactMessageModel;

  /**
   * Getter for the refresh navbar subject
   * @returns Subject<void> - Observable subject for navbar refresh events
   */
  get getRefreshNavbar$(): Subject<void> {
    return this.refreshNavbar$;
  }

  /**
   * Initializes the service with required dependencies
   * @param http - HTTP client for making API requests
   * @param configService - Service for accessing application configuration
   * @param dialog - Material dialog service for showing dialogs
   * @param localStorage - Service for managing local storage
   * @param sessionStorage - Service for managing session storage
   */
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    public dialog: MatDialog,
    private localStorage: LocalStorageService,
    private sessionStorage: SessionStorageService,
    private dataService: DataService
  ) {}

  setUmsUrl() {
    this.umsUrl = this.configService.applicationConfig.UmsURL;
  }

  /**
   * Sets the selected profile ID in local storage
   * This is used to maintain the user's selected profile across sessions
   * @param contactId - The ID of the contact to set as selected
   */
  set selectedProfile(contact: ContactMessageModel) {
    this.localStorage.setItem(KEYS.SELECTED_PROFILE_ID, contact.contactID);
    this.localStorage.setItem(KEYS.SELECTED_PROFILE, JSON.stringify(contact));
    this._selectedProfile = contact;
    this._selectedProfileSubject.next(contact);
  }

  /**
   * Gets the currently selected profile ID from local storage
   * @returns string - The selected profile ID or empty string if not set
   */
  get selectedProfile() {
    return this._selectedProfile;
  }

  /**
   * Sets the main profile ID in session storage
   * The main profile is typically the primary account holder
   * @param contactId - The ID of the contact to set as main profile
   */
  public setMainProfile(contactId: string): void {
    this.sessionStorage.setItem(KEYS.MAIN_CONTACT_ID, contactId);
  }

  /**
   * Gets the access token from local storage or falls back to UMS API token
   * Handles cases where the token might be undefined or invalid
   * @returns string - The access token to use for API requests
   */
  private getAccessToken(): string {
    const token = this.getEvergentAccessToken();
    return Boolean(token?.length)
      ? token
      : this.configService.applicationConfig.umsApiToken;
  }

  private getEvergentAccessToken(): string | null {
    return this.localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN);
  }

  /**
   * Creates HTTP headers with authorization and content type
   * Standardizes header creation for all API requests
   * @returns HttpHeaders - Headers for API requests
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'content-type': 'application/json',
      Authorization: `Bearer ${this.getAccessToken()}`,
    });
  }

  public getAccountProfileRequest(): Observable<any> {
    this.setUmsUrl();
    const url = getApiUrl(this.umsUrl, 'GET_CONTACT');
    const requestMessage = {
      ...getBaseRequestData(this.configService.applicationConfig),
    };

    return this.http
      .post<any>(url, requestMessage, {
        headers: this.getHeaders(),
      })
      .pipe(tap(() => this.refreshNavbar$.next()));
  }

  public getAccountProfileUserRequest(): Observable<GetAccountProfileUserResponseMessage> {
    this.setUmsUrl();
    const url = getApiUrl(this.umsUrl, 'GET_ACCOUNT_PROFILE');
    const requestContent = {
      ...new AccountProfileSettingsRequestModel(),
      ...getBaseRequestData(this.configService.applicationConfig),
    };

    const getAccountProfile = new GetAccountProfileRequestMessage();
    getAccountProfile.GetAccountProfileRequestMessage = requestContent;

    return this.http
      .post<GetAccountProfileUserResponseMessage>(url, getAccountProfile, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap((response: GetAccountProfileUserResponseMessage) => {
          if (
            !isSuccessfulResponse(response.GetAccountProfileResponseMessage)
          ) {
            throwError(
              () => new Error('Failed to get account profile: Invalid response')
            );
          }
          this.processAccountProfileResponse(response);
          return;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  private processAccountProfileResponse(
    response: GetAccountProfileUserResponseMessage
  ): void {
    const finalCatalogues = this.extractCatalogues(response);

    response.GetAccountProfileResponseMessage.subscribedCatalogue =
      finalCatalogues.length ? finalCatalogues : null;

    this.accountProfile = response;
    this.updateSubscriptionStatus(response);
  }

  private extractCatalogues(
    response: GetAccountProfileUserResponseMessage
  ): string[] {
    const catalogues =
      response?.GetAccountProfileResponseMessage?.subscribedCatalogue || [];
    const subscribedProductCodes =
      response?.GetAccountProfileResponseMessage?.subscribedProductCodes || [];
    this.localStorage.setItem(KEYS.SUBSCRIPTION_PACKAGE, catalogues.join(','));
    this.localStorage.setItem(
      KEYS.SUBSCRIPTION_PRODUCT_CODES,
      subscribedProductCodes.join(',')
    );
    return catalogues
      .reduce(
        (acc, catalogue) => [...acc, ...catalogue.split(',')],
        [] as string[]
      )
      .filter(Boolean);
  }

  private updateSubscriptionStatus(
    response: GetAccountProfileUserResponseMessage
  ): void {
    const subscriptionStatus =
      response.GetAccountProfileResponseMessage.subscriptionStatus;
    if (subscriptionStatus) {
      this.localStorage.setItem(
        KEYS.SUBSCRIPTION_STATUS,
        subscriptionStatus.toString()
      );
    }
  }

  public getContactProfile(
    contactId?: string,
    locale?: string
  ): Observable<GetContactResponseMessage> {
    this.setUmsUrl();
    const url = getApiUrl(this.umsUrl, 'GET_CONTACT');
    const getContactRequestMessage = this.getContactRequestMessage(
      contactId,
      locale
    );

    return this.http
      .post<GetContactResponseMessage>(url, getContactRequestMessage, {
        headers: this.getHeaders(),
      })
      .pipe(
        tap((response: GetContactResponseMessage) => {
          if (!isSuccessfulResponse(response.GetContactResponseMessage)) {
            throwError(
              () => new Error('Failed to get contact profile: Invalid response')
            );
            return;
          }
          this.processContactProfileResponse(response, contactId);
          return;
        }),
        catchError((error) => {
          console.error('Error fetching contact profile:', error);
          return throwError(() => error);
        })
      );
  }

  private processContactProfileResponse(
    data: GetContactResponseMessage,
    contactId?: string
  ): void {
    this.storeChannelPartnerInfo(data);
    this.processContactMessages(data);
    this.updateSelectedProfile(data, contactId);
    this.setProfilePreferences(this.contactsResponse);
  }

  public updateProfile(
    data: UpdateProfileRequest
  ): Observable<UpdateProfileResponseMessage> {
    this.setUmsUrl();
    const url = getApiUrl(this.umsUrl, 'UPDATE_PROFILE');
    const requestMessage = this.getUpdateProfileRequestMessage(data);
    return this.http.post<UpdateProfileResponseMessage>(url, requestMessage, {
      headers: this.getHeaders(),
    });
  }

  private storeChannelPartnerInfo(data: GetContactResponseMessage): void {
    this.sessionStorage.setItem(
      KEYS.ACCOUNT_CHANNEL_PARTNER_ID,
      data?.GetContactResponseMessage?.channelPartnerID
    );
    const cpCustomerID = data?.GetContactResponseMessage?.cpCustomerID;
    this.localStorage.setItem(KEYS.CP_CUSTOMER_ID, cpCustomerID);
  }

  private processContactMessages(data: GetContactResponseMessage): void {
    let additionalProfiles = 1;
    data.GetContactResponseMessage.contactMessage?.forEach((item) => {
      item.index = this.getContactIndex(
        item.contactType?.toLowerCase(),
        additionalProfiles
      );
      if (!['0', '1', 'Kids'].includes(item.index)) additionalProfiles++;
    });
    this.contactsResponse = data.GetContactResponseMessage.contactMessage;
  }

  private getContactIndex(
    contactType: string,
    additionalProfiles: number
  ): string {
    switch (contactType) {
      case 'profile':
        return '0';
      case 'family':
        return '1';
      case 'kids':
        return 'Kids';
      default:
        return (additionalProfiles + 1).toString();
    }
  }

  private updateSelectedProfile(
    data: GetContactResponseMessage,
    contactId?: string
  ): void {
    const main = data?.GetContactResponseMessage?.contactMessage?.find(
      (item) => item.main === true
    );
    if (main) {
      this.sessionStorage.setItem(KEYS.MAIN_CONTACT_ID, main.contactID);
      this.sessionStorage.setItem(KEYS.MAIN_CONTACT, JSON.stringify(main));
    }

    contactId = this.localStorage.getItem(KEYS.SELECTED_PROFILE_ID);
    this.selectedProfile = contactId
      ? data.GetContactResponseMessage.contactMessage.find(
          (it) => it.contactID === contactId
        )
      : main;

    const hasLanguage = !!this.selectedProfile?.primarySpeakingLanguage;
    if (hasLanguage) {
      // Convert language names (e.g., "Tamil, Telugu") to codes (e.g., "ta, te")
      const preferredLanguageCodes = this.convertLanguageNamesToCodes(
        this.selectedProfile.primarySpeakingLanguage
      );
      this.localStorage.setItem(
        KEYS.PREFERRED_LANGUAGES,
        preferredLanguageCodes
      );
      this.dataService.skipPreferredLanguageSelection = true;
    } else if (this.dataService.isFromDeeplink) {
      // Silent skip for Deeplinks: If we are in a deeplink journey and profile is missing languages,
      // we skip the popup and set temporary languages to avoid conflicts with anonymous users.
      this.dataService.skipPreferredLanguageSelection = true;
      this.localStorage.setItem(KEYS.DL_PREFERRED_LANGUAGES, 'te,ta');
    } else {
      this.dataService.skipPreferredLanguageSelection = false;
    }
    if (this.selectedProfile) {
      this.dataService.isParentalEnabled = this.selectedProfile.parentalControl;
    } else {
      this.dataService.isParentalEnabled = false;
    }

    this.localStorage.setItem(
      KEYS.SELECTED_PROFILE,
      JSON.stringify(this.selectedProfile)
    );
  }

  /**
   * Gets a contact by its ID
   * @param contactId - The ID of the contact to find
   * @returns ContactMessageModel | undefined - The found contact or undefined if not found
   */
  getContactById(contactId: string): ContactMessageModel | undefined {
    if (!this.contactsResponse) {
      this.getContactProfile();
    }
    return this.contactsResponse.find((r) => r.contactID === contactId);
  }

  /**
   * Gets a contact by its name
   * @param contactName - The name of the contact to find
   * @param contactId - The ID of the contact to exclude from search
   * @returns ContactMessageModel | undefined - The found contact or undefined if not found
   */
  getContactByName(
    contactName: string,
    contactId: string
  ): ContactMessageModel | undefined {
    if (!this.contactsResponse) {
      this.getContactProfile();
    }
    return this.contactsResponse.find(
      (r) =>
        r.firstName.toLowerCase() === contactName.toLowerCase() &&
        contactId !== r.contactID
    );
  }

  /**
   * Creates a contact request message
   * @param contactId - Optional ID of the contact
   * @param locale - Optional locale for the request
   * @returns GetContactRequestMessage - The formatted request message
   */
  private getContactRequestMessage(
    contactId?: string,
    locale?: string
  ): GetContactRequestMessage {
    const requestMessage = new GetContactRequestMessage();
    const requestContent = {
      ...new GetContactRequestModel(),
      contactID: contactId,
      locale: locale,
      ...getBaseRequestData(this.configService.applicationConfig),
    };

    requestMessage.GetContactRequestMessage = requestContent;
    return requestMessage;
  }

  private getUpdateProfileRequestMessage(
    data: UpdateProfileRequest
  ): UpdateProfileRequestMessage {
    const requestMessage = new UpdateProfileRequestMessage();
    requestMessage.UpdateProfileRequestMessage = {
      ...data,
      ...getBaseRequestData(this.configService.applicationConfig),
    };
    return requestMessage;
  }

  private setProfilePreferences(profiles: ContactMessageModel[]): void {
    const contacts =
      JSON.parse(localStorage.getItem(KEYS.PROFILE_PREFERENCES)) ?? {};
    profiles.forEach((contact) => {
      if (!contacts[contact.contactID]) {
        contacts[contact.contactID] = {
          autoPlayOnLoad: true,
          preferredAudioLanguage: {
            name: 'English',
            key: 'en',
            langClass: 'english-photo',
          },
          preferredTextLanguage: {
            name: 'English',
            key: 'en',
            langClass: 'english-photo',
          },
        };
      }
    });
    localStorage.setItem(KEYS.PROFILE_PREFERENCES, JSON.stringify(contacts));
  }

  getSelectedProfilePreferences(): ProfilePreferences | undefined {
    const profileID = this.selectedProfile.contactID;
    return this.getAllProfilePreferences()?.[profileID];
  }

  getAllProfilePreferences(): unknown {
    const profilePreferences = localStorage.getItem(KEYS.PROFILE_PREFERENCES);
    if (profilePreferences) {
      return JSON.parse(profilePreferences);
    }
    return undefined;
  }

  initializeAccountAndProfiles() {
    return forkJoin([
      this.getAccountProfileUserRequest(),
      this.getContactProfile(),
    ]).toPromise();
  }

  get account() {
    return this.accountProfile;
  }

  get contacts() {
    return this.contactsResponse;
  }

  get mainProfile(): ContactMessageModel | undefined {
    return this.contactsResponse?.find((it) => it.main);
  }

  mainContact() {
    return this.contacts?.find((it) => it.main);
  }

  maskedMobileNumber() {
    const phone = this.mainContact().mobileNumber;
    if (!phone) return null;
    return phone.replace(/^(\d{3})(\d{4})(\d{3})$/, (match, p1, p2, p3) => {
      return `+91 ${p1}${'*'.repeat(p2.length)}${p3}`;
    });
  }

  maskedMobileNumberHtml(): string | null {
    const countryCode = this.getCountryDialCode();
    const phone = this.mainContact()?.mobileNumber;
    if (!phone || phone.length !== 10) return null;

    const part1 = phone.slice(0, 3);
    const masked = '<span>****</span>';
    const part3 = phone.slice(7);

    return `${countryCode} ${part1}${masked}${part3}`;
  }

  private getCountryDialCode(): string {
    try {
      const countries = countryCodesData.countries;

      // Find the country by name and return its dial code
      const country =
        this.accountProfile.GetAccountProfileResponseMessage
          .countryOfRegistration;

      const countryData = countries.find(
        (c: any) => c.code.toLowerCase() === country.toLowerCase()
      );

      return countryData ? countryData.dialCode : '+91'; // Default to India if not found
    } catch (error) {
      console.error('Error reading country code data:', error);
      return '+91'; // Default to India if error occurs
    }
  }

  maskedEmailHtml(): string | null {
    const email = this.account.GetAccountProfileResponseMessage?.email;
    if (!email) return null;

    const [localPart, domain] = email.split('@');
    if (!domain) return email;

    const trimmedLocalPart =
      localPart.length > 50 ? localPart.slice(0, 50) : localPart;

    const visiblePart = trimmedLocalPart.slice(0, 2);
    const maskedLength = trimmedLocalPart.length - visiblePart.length;
    const maskedSpan = `<span>${'*'.repeat(maskedLength)}</span>`;

    return `${visiblePart}${maskedSpan}@${domain}`;
  }

  maskedEmail() {
    const email = this.account.GetAccountProfileResponseMessage?.email;
    if (!email) return null;
    const [localPart, domain] = email.split('@');
    if (!domain) return email;
    const trimmedLocalPart =
      localPart.length > 50 ? localPart.slice(0, 50) : localPart;
    const maskedLocalPart =
      trimmedLocalPart.length > 2
        ? `${trimmedLocalPart.slice(0, 2)}${'*'.repeat(
            trimmedLocalPart.length - 2
          )}`
        : trimmedLocalPart;
    return `${maskedLocalPart}@${domain}`;
  }

  getUserInfo(): any {
    const userId = this.localStorage.getItem(KEYS.CP_CUSTOMER_ID) || '';
    const profileId = this.localStorage.getItem(KEYS.SELECTED_PROFILE_ID) || '';
    const token = this.getEvergentAccessToken() || '';
    const userInfo = {
      id: userId,
      type: Boolean(token.length) ? 'registered' : 'anonymous',
      subscription: undefined,
      profileId,
    };
    return userInfo;
  }

  /**
   * Converts language names to language codes
   * Example: "Tamil, Telugu" -> "ta, te"
   */
  private convertLanguageNamesToCodes(languageNames: string): string {
    const languageMap: { [key: string]: string } = {
      Tamil: 'ta',
      Telugu: 'te',
    };

    return languageNames
      .split(',')
      .map((lang) => {
        const trimmedLang = lang.trim();
        return languageMap[trimmedLang] || trimmedLang;
      })
      .join(',');
  }
}
